import React, { useEffect, useMemo, useState } from "react";
import { Search, User, Mail, CheckCircle, XCircle, UserCog } from "lucide-react";
import {
  GetAllUsers,
  ActivateUser,
  DeactivateUser,
  AssignManager
} from "../services/UserManagementService";
import NotificationAlert from "../../../CustomAlerts/NotificationAlert";

const ManageUsers = () => {
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [alert, setAlert] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [showManagerModal, setShowManagerModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedManagerCode, setSelectedManagerCode] = useState("");

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await GetAllUsers();
      setUsers(res?.users || []);
    } catch (error) {
      setAlert({
        type: "error",
        message:
          error?.message || "Failed to fetch users. Please try again later.",
      });
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleToggleStatus = async (user) => {
    try {
      setActionLoading(true);
      let res;
      if (user.isActive) {
        res = await DeactivateUser(user.userUID);
      } else {
        res = await ActivateUser(user.userUID);
      }

      if (res?.status === "success") {
        setUsers((prev) =>
          prev.map((u) =>
            u.userUID === user.userUID
              ? {
                  ...u,
                  isActive: !u.isActive,
                }
              : u,
          ),
        );
      } else {
        setAlert({
          type: "error",
          message: res?.message || "Failed to update user status.",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error?.message || "Something went wrong.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignManager = async () => {
    if (!selectedUser || !selectedManagerCode) {
      setAlert({
        type: "error",
        message: "Please select a manager.",
      });

      return;
    }

    try {
      setActionLoading(selectedUser.userUID);

      const res = await AssignManager(
        selectedUser.userUID,
        selectedManagerCode,
      );

      if (res?.status === "success") {
        const selectedManager = managers.find(
          (m) => m.employeeCode === selectedManagerCode,
        );

        setUsers((prev) =>
          prev.map((u) =>
            u.userUID === selectedUser.userUID
              ? {
                  ...u,
                  managerName: selectedManager?.userName || "-",
                  managerCode: selectedManagerCode,
                }
              : u,
          ),
        );

        setAlert({
          type: "success",
          message: res?.message || "Manager assigned successfully.",
        });

        setShowManagerModal(false);

        setSelectedUser(null);

        setSelectedManagerCode("");
      } else {
        setAlert({
          type: "error",
          message: res?.message || "Failed to assign manager.",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error?.message || "Something went wrong.",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter((item) => {
      const query = search.toLowerCase();

      const matchesSearch =
        !query ||
        item.userName?.toLowerCase().includes(query) ||
        item.email?.toLowerCase().includes(query) ||
        item.role?.toLowerCase().includes(query);

      const matchesStatus =
        !statusFilter ||
        (statusFilter === "Active" && item.isActive) ||
        (statusFilter === "Inactive" && !item.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [users, search, statusFilter]);

  const totalUsers = filteredUsers.length;
  const totalPages = Math.ceil(totalUsers / pageSize);
  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;

    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, page, pageSize]);

  const activeUsers = users.filter((x) => x.isActive).length;
  const inactiveUsers = users.filter((x) => !x.isActive).length;
  
  const managers = useMemo(() => {
    return users.filter(
      (u) => 
        u.isActive &&
        u.employeeCode
    );
  }, [users]);

  return (
    <>
      <div className="p-2 space-y-3">
        {/* METRICS */}

        <div className="grid grid-cols-3 gap-3">
          <MetricCard
            title="Total Users"
            value={users.length}
            icon={<User size={18} />}
          />

          <MetricCard
            title="Active"
            value={activeUsers}
            icon={<CheckCircle size={18} />}
            color="emerald"
          />

          <MetricCard
            title="Inactive"
            value={inactiveUsers}
            icon={<XCircle size={18} />}
            color="red"
          />
        </div>

        {/* FILTER */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-3">
          <div className="flex items-center gap-3">
            {/* SEARCH */}
            <div className="relative w-[320px]">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                placeholder="Search users..."
                className="w-full pl-10 pr-3 h-10 rounded-xl border border-slate-200 bg-slate-50 text-sm outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>

            {/* STATUS */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
              className="h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm min-w-[130px]"
            >
              <option value="">All Users</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>

            {/* ROWS */}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-slate-500 whitespace-nowrap">
                Rows
              </span>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPage(1);
                  setPageSize(Number(e.target.value));
                }}
                className="h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm min-w-[80px]"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-0 px-6 py-4 bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-semibold text-slate-500 items-center">
            <div className="col-span-3 flex items-center">User</div>
            <div className="col-span-3 flex items-center">Email</div>
            <div className="col-span-2 flex items-center">Department</div>
            <div className="col-span-2 flex items-center">Manager</div>
            <div className="col-span-1 flex items-center justify-center">Status</div>
            <div className="col-span-1 flex items-center justify-center">Actions</div>
          </div>

          <div className="divide-y divide-slate-100">
            {loading && initialLoading ? (
              <div className="py-16 text-center">Loading users...</div>
            ) : paginatedUsers.length === 0 ? (
              <div className="py-16 text-center">
                <User size={28} className="mx-auto text-slate-400 mb-3" />

                <p className="text-slate-600">No users found</p>
              </div>
            ) : (
              paginatedUsers.map((item) => (
                <div
                  key={item.userUID}
                  className="grid grid-cols-12 px-6 py-4 hover:bg-blue-50/40 transition-all"
                >
                  {/* USER */}
                  <div className="col-span-3 min-w-0 flex gap-3 items-center">
                    <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-700 font-semibold shrink-0">
                      {item.userName?.charAt(0)}
                    </div>

                    <div className="overflow-hidden">
                      <p className="font-semibold text-sm text-slate-800 truncate">
                        {item.userName}
                      </p>
                    </div>
                  </div>

                  {/* EMAIL */}
                  <div className="col-span-3 min-w-0 flex items-center">
                    <div className="flex items-center gap-2 text-sm text-slate-600 truncate">
                      <Mail size={13} className="text-slate-400 shrink-0" />
                      <span className="truncate">{item.email}</span>
                    </div>
                  </div>

                  {/* ROLE */}
                  <div className="col-span-2 min-w-0 flex items-center">
                    <span className="px-3 py-1 rounded-lg bg-slate-100 border border-slate-200 text-xs font-medium text-slate-700">
                      {item.department || "-"}
                    </span>
                  </div>

                  {/* MANAGER */}
                  <div className="col-span-2 min-w-0 flex items-center text-sm text-slate-700">
                    {item.managerName || "-"}
                  </div>

                  {/* STATUS */}
                  <div className="col-span-1 min-w-0 flex items-center justify-center">
                    <button
                      onClick={() => handleToggleStatus(item)}
                      disabled={actionLoading === item.userUID}
                      aria-pressed={item.isActive}
                      className={`
                                  relative w-[88px] h-7 rounded-full border-none overflow-hidden
                                  transition-colors duration-300
                                  ${item.isActive ? "bg-primary" : "bg-slate-300"}
                                  ${actionLoading === item.userUID ? "opacity-55 cursor-not-allowed" : ""}
                                `}
                    >

                      {/* ACTIVE label */}
                      <span
                        className={`
                                    absolute inset-0 flex items-center justify-start pl-[11px]
                                    text-[10.5px] font-bold tracking-wider text-white pointer-events-none
                                    transition-all duration-200
                                    ${item.isActive ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-1.5"}
                                  `}
                      >
                        ACTIVE
                      </span>

                      {/* INACTIVE label */}
                      <span
                        className={`
                                    absolute inset-0 flex items-center justify-end pr-[9px]
                                    text-[10.5px] font-bold tracking-wider text-slate-600 pointer-events-none
                                    transition-all duration-200
                                    ${item.isActive ? "opacity-0 translate-x-1.5" : "opacity-100 translate-x-0"}
                                  `}
                      >
                        INACTIVE
                      </span>

                      {/* CIRCLE */}
                      <div
                        className={`
                                    absolute top-[3px] w-[22px] h-[22px] rounded-full bg-white
                                    shadow-md pointer-events-none
                                    transition-transform duration-300 ease-[cubic-bezier(0.34,1.45,0.64,1)]
                                    ${item.isActive ? "translate-x-[63px]" : "translate-x-[3px]"}
                                  `}
                      />
                    </button>
                  </div>

                  {/* ACTIONS */}
                  <div className="col-span-1 min-w-0 flex items-center justify-center">
                    <button
                      title="Assign Manager"
                      onClick={() => {
                        setSelectedUser(item);
                        setSelectedManagerCode(item.managerCode || "");
                        setShowManagerModal(true);
                      }}
                      className="h-8 w-8 rounded-lg border border-slate-200 hover:bg-slate-100 flex items-center justify-center transition-all"
                    >
                      <UserCog size={15} strokeWidth={2} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ================= PAGINATION ================= */}

          {!initialLoading && totalPages >= 1 && (
            <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 bg-white">
              <div className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                {/* PREV */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((prev) => prev - 1)}
                  className={`h-8 px-3 rounded-lg text-sm font-medium transition-all
                              ${
                                page === 1
                                  ? "text-slate-300 cursor-not-allowed"
                                  : "text-slate-500 hover:bg-slate-100"
                              }
                            `}
                >
                  Prev
                </button>

                {/* PAGE NUMBERS */}
                {(() => {
                  const pages = [];

                  for (let i = 1; i <= totalPages; i++) {
                    if (
                      i === 1 ||
                      i === totalPages ||
                      (i >= page - 1 && i <= page + 1)
                    ) {
                      pages.push(i);
                    }
                  }

                  return pages.map((pageNumber, index) => {
                    const prevPage = pages[index - 1];

                    return (
                      <React.Fragment key={pageNumber}>
                        {prevPage && pageNumber - prevPage > 1 && (
                          <span className="px-1 text-sm text-slate-400">
                            ...
                          </span>
                        )}

                        <button
                          onClick={() => setPage(pageNumber)}
                          className={`h-8 min-w-[34px] px-2 rounded-lg text-sm font-semibold transition-all
                                      ${
                                        page === pageNumber
                                          ? "bg-primary text-white shadow-sm"
                                          : "text-slate-600 hover:bg-slate-100"
                                      }
                                    `}
                        >
                          {pageNumber}
                        </button>
                      </React.Fragment>
                    );
                  });
                })()}

                {/* NEXT */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((prev) => prev + 1)}
                  className={`h-8 px-3 rounded-lg text-sm font-medium transition-all
                              ${
                                page === totalPages
                                  ? "text-slate-300 cursor-not-allowed"
                                  : "text-slate-500 hover:bg-slate-100"
                              }
                            `}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showManagerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 backdrop-blur-sm px-4">
          <div
            className="w-full max-w-md overflow-hidden rounded-3xl bg-white border border-slate-200 shadow-[0_20px_60px_rgba(15,23,42,0.18)]
                       animate-in fade-in zoom-in-95 duration-200"
          >
            {/* HEADER */}
            <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100 bg-gradient-to-b from-primaryLight to-white">
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  Assign Manager
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  Select a reporting manager for this employee.
                </p>
              </div>

              <button
                onClick={() => {
                  setShowManagerModal(false);
                  setSelectedUser(null);
                  setSelectedManagerCode("");
                }}
                className="h-8 w-8 rounded-xl hover:bg-red-100 text-slate-500 flex items-center justify-center transition-all"
              >
                ✕
              </button>
            </div>

            {/* BODY */}
            <div className="p-6 space-y-5">

              {/* USER CARD */}
              <div className="flex items-center gap-3 p-4 rounded-2xl border border-primaryBorder bg-primaryLight">
                <div className="h-11 w-11 rounded-xl bg-white text-primary flex items-center justify-center font-semibold shadow-sm">
                  {selectedUser?.userName?.charAt(0)}
                </div>

                <div className="min-w-0">
                  <p className="text-sm text-slate-500">
                    Assigning manager for
                  </p>

                  <p className="font-semibold text-slate-800 truncate">
                    {selectedUser?.userName}
                  </p>
                </div>
              </div>

              {/* SELECT */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Select Manager
                </label>

                <select
                  value={selectedManagerCode}
                  onChange={(e) => setSelectedManagerCode(e.target.value)}
                  className="w-full h-12 rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-700 outline-none transition-all
                           focus:border-primary focus:ring-4 focus:ring-primary/10"
                >
                  <option value="">Choose Manager</option>

                  {managers.map((manager) => (
                    <option key={manager.userUID} value={manager.employeeCode}>
                      {manager.userName} • {manager.employeeCode}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* FOOTER */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/70">
              <button
                onClick={() => {
                  setShowManagerModal(false);
                  setSelectedUser(null);
                  setSelectedManagerCode("");
                }}
                className="px-4 h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-100 transition-all"
              >
                Cancel
              </button>

              <button
                onClick={handleAssignManager}
                disabled={!selectedManagerCode}
                className="px-5 h-11 rounded-xl bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all shadow-sm"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

const MetricCard = ({ title, value, icon, color = "blue" }) => {
  const colorMap = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    red: "bg-red-50 text-red-600",
  };

  return (
    <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <h3 className="text-3xl font-semibold text-slate-900 mt-2">{value}</h3>
        </div>

        <div className={`h-12 w-12 rounded-3xl flex items-center justify-center ${colorMap[color] || colorMap.blue}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default ManageUsers;
